import { useOutletContext } from "react-router-dom";
import { Box, Divider, Grid, TextField, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { EditNoteOutlined } from "@mui/icons-material";
import useCircuitoForm from "./hooks/useCircuitoForm";
import useCircuitoTags from "./hooks/useCircuitoTags";
import useCircuitoAnexosSave from "./hooks/useCircuitoAnexosSave";
import useCircuitoSave from "./hooks/useCircuitoSave";
import useDiagramaState from "./diagramaEquipamentos/hooks/useDiagramaState";
import useNodeAnexos from "./diagramaEquipamentos/hooks/useNodeAnexos";
import useStyles from "./styles";
import FormularioTab from "./FormularioTab";
import AnexosTab from "./AnexosTab";
import DiagramaTab from "./DiagramaTab";
import CircuitoTabs from "./CircuitoTabs";
import { useAplicarEmMassaContext } from "../aplicarEmMassaContext";

export default function InternoCircuitoCliente({
  serviceId = "",
  ownerClienteId = null,
  // Em edição todas as abas de circuito ficam montadas (display:none); só a aba
  // visível pode capturar o Ctrl+V, senão o anexo entra em todos os circuitos
  isActive = true,
  // Posição do circuito entre as abas do serviço; usada só para o placeholder
  // genérico do campo "Nome do circuito" (labelCircuito em FormularioTab)
  index = 0,
  data = {},
  delivery = {},
  // POP/Regional são globais e vêm prontos do pai (2.3-circuito/index.js) — buscados
  // uma vez para todos os circuitos do serviço, não por circuito (etapa 14 do plano)
  popTags: popTagsProp = [],
  regionalTags: regionalTagsProp = [],
  loadingTags = false,
  // Serial da ONU: busca única e deduplicada por serviço já vive em serviceItem.js
  loadingSerial = false,
  optionsRoteamento = [],
  updateOptions = () => {},
  optionsEntregaFinal = [],
  optionsEntregaFinalCliente = [],
  setOptionsEntregaFinalCliente = () => {},
  setOptionsEntregaFinal = () => {},
  setOptionsRoteamento = () => {},
  onFormUpdate = () => {},
  setChanged = () => {},
  editMode = false,
  setEditMode = () => {},
  showAllContacts = false,
  setShowAllContacts = () => {},
  clientCpes = {},
  clientHosts = {},
  // Reporta handleSave/handleReset/loading ao cabeçalho de serviceItem.js (sem ref, Task 7)
  setCircuitoActions = () => {},
  // Aba que o usuário estava vendo no preview antes de entrar em edição: preserva a aba
  // ativa ao clicar "Editar" (evita saltar de Diagrama para Formulário)
  abaInicialEditar = 0,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const { setSnackbar, selectedCliente: clienteInfo } = useOutletContext();

  // Opções do combobox de host do diagrama de equipamentos: hosts do cliente
  // agregados das 4 categorias (CPE/ONU/Link/Sessão), já filtrados pelo serviço
  const hostOptions = clientHosts?.[serviceId] || [];

  // Estado principal do formulário (prevData/formData/errors) e handlers de
  // mudança/validação — extraídos para useCircuitoForm (Task 4 da decomposição)
  const {
    prevData,
    setPrevData,
    formData,
    setFormData,
    errors,
    handleChange,
    handleNestedChange,
    validateBlocoAndIp,
    temVelocidade,
    restaurarPrevData,
  } = useCircuitoForm({ data, delivery, serviceId, setChanged });

  // Loading do circuito aberto: liga/desliga em useCircuitoSave e em OpcoesEquipamento (via FormularioTab)
  const [loading, setLoading] = useState(false);

  const [isDiagramExpanded, setIsDiagramExpanded] = useState(false);

  // Aba ativa das três abas do circuito (0 Formulário, 1 Anexos, 2 Diagrama) — também dona do gate de isActive do Ctrl+V de cada aba
  const [abaAtiva, setAbaAtiva] = useState(abaInicialEditar);

  // Ref para o useDiagramaState acessar o nodeAnexos (instanciado logo depois, mesma ordem que antes vivia em DiagramaCanvas) sem ciclo de renderização
  const nodeAnexosRef = useRef(null);

  // useCallback com deps vazias: só lê nodeAnexosRef.current (ref, sempre estável), então a
  // identidade do callback não pode mudar a cada render — senão resetDiagramData/handleReset
  // (que dependem dele) ficam instáveis e o report pro cabeçalho entra em loop (Task 7)
  const onResetAnexos = useCallback(
    () => nodeAnexosRef?.current?.descartarPendentes?.(),
    [],
  );
  const onCapturarSnapshotExtras = useCallback(
    () => nodeAnexosRef?.current?.capturarAnexosSnapshot?.(),
    [],
  );
  const onRestaurarSnapshotExtras = useCallback(
    (snapshot) => nodeAnexosRef?.current?.restaurarAnexosSnapshot?.(snapshot),
    [],
  );

  // Consome sinal de aplicação em massa para reinicializar o diagrama quando o circuito aberto for afetado
  const { reapplySignal = 0 } = useAplicarEmMassaContext();

  // Identificador da instância do diagrama: composto pelo id/tempId do circuito e pelo sinal
  // dedicado de aplicação em massa (reapplySignal), NUNCA por updatedAt de ClientCircuitDiagram —
  // updatedAt muda a cada save normal do circuito e causaria reset indevido com perda de anexos retidos
  const diagramInstanceKey = `${data?.id || data?.tempId}-${reapplySignal}`;

  // Estado do diagrama mora no circuito, não na aba: a aba desmonta e o Salvar precisa dos nodes/edges mesmo que ela nunca tenha sido aberta
  const diagrama = useDiagramaState({
    data,
    diagramInstanceKey,
    editMode: editMode && !loading,
    onResetAnexos,
    onCapturarSnapshotExtras,
    onRestaurarSnapshotExtras,
  });

  // Ref sempre sincronizado com nodes para callbacks assíncronos do diagrama (getPendingNodeAnexos/refreshNodeAnexosAfterSave)
  const nodesRef = useRef(diagrama?.nodes);
  useEffect(() => {
    nodesRef.current = diagrama?.nodes;
  }, [diagrama?.nodes]);

  const nodeAnexos = useNodeAnexos({
    circuitoId: formData?.id || "",
    circuitoPersisted: Boolean(formData?.id),
    editMode: editMode && !loading,
    selectedNodeId: diagrama?.selectedNodeId,
    nodesRef,
    setSnackbar,
  });
  nodeAnexosRef.current = nodeAnexos;

  const {
    circuitoAnexos,
    setCircuitoAnexos,
    deletedAnexos,
    setDeletedAnexos,
    revokeNewFilesPreviews,
    fetchAnexosCircuito,
  } = useCircuitoAnexosSave({ circuitoId: formData?.id });

  // Valor selecionado de cada grupo de equipamento; o CRUD/busca/menu de cada
  // grupo vive em OpcoesEquipamento (useOpcoesEquipamento), mas a opção
  // selecionada final (usada no upsert em handleSave) é lida aqui
  const [valueRoteamento, setValueRoteamento] = useState(null);
  const [valueEntregaFinal, setValueEntregaFinal] = useState(null);
  const [valueEntregaFinalCliente, setValueEntregaFinalCliente] =
    useState(null);

  useEffect(() => {
    handleGet();
  }, []);

  // Manipuladores de ações
  async function handleGet() {
    try {
      setLoading(true);
      setValueRoteamento(
        formData?.equipRouter?.[0] &&
          typeof formData?.equipRouter[0] === "object"
          ? formData?.equipRouter[0]
          : null,
      );
      setValueEntregaFinal(
        formData?.equipDelivery?.[0] &&
          typeof formData?.equipDelivery[0] === "object"
          ? formData?.equipDelivery[0]
          : null,
      );
      setValueEntregaFinalCliente(
        formData?.equipLastMile?.[0] &&
          typeof formData?.equipLastMile[0] === "object"
          ? formData?.equipLastMile[0]
          : null,
      );
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao obter dados de circuito",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  /** Tags POP/Regional (listas em si vêm do pai — ver popTagsProp/regionalTagsProp);
   * levantado para antes do save porque useCircuitoSave precisa de selectedPopId/
   * selectedRegionalId já resolvidos no momento em que é chamado */
  const popTags = popTagsProp;
  const regionalTags = regionalTagsProp;
  const {
    selectedPopId,
    handlePopSelect,
    handlePopCreate,
    selectedRegionalId,
    handleRegionalSelect,
    handleRegionalCreate,
    handleTagUpdate,
    handleTagDelete,
  } = useCircuitoTags({ popTags, regionalTags, data });

  // Orquestra o save completo do circuito (I3-I6: upsert + anexos de circuito e
  // de nó do diagrama via Promise.allSettled) — extraído para useCircuitoSave sem
  // reescrever a lógica (Task 5 da decomposição)
  const { handleSave } = useCircuitoSave({
    formData,
    setFormData,
    setPrevData,
    validateBlocoAndIp,
    valueRoteamento,
    valueEntregaFinal,
    valueEntregaFinalCliente,
    circuitoAnexos,
    setCircuitoAnexos,
    deletedAnexos,
    setDeletedAnexos,
    revokeNewFilesPreviews,
    getDiagramData: diagrama?.getDiagramData,
    resetDiagramData: diagrama?.resetDiagramData,
    getPendingNodeAnexos: nodeAnexos?.getPendingNodeAnexos,
    refreshNodeAnexosAfterSave: nodeAnexos?.refreshNodeAnexosAfterSave,
    serviceId,
    ownerClienteId,
    clienteInfo,
    selectedPopId,
    selectedRegionalId,
    onFormUpdate,
    setSnackbar,
    // Leva de volta à aba Formulário em falha de validação (ver comentário em useCircuitoSave.js)
    onTrocarAba: setAbaAtiva,
    setLoading,
    setEditMode,
    setChanged,
  });

  // useCallback só para não recriar a cada render (a nova useEffect de report abaixo depende dela)
  const handleReset = useCallback(() => {
    restaurarPrevData();
    setEditMode(false);
    setChanged(false);
    setShowAllContacts(false);
    setDeletedAnexos([]);
    fetchAnexosCircuito();
    // Reverte o diagrama para o último estado salvo, sem chamada de rede
    diagrama?.resetDiagramData?.(
      prevData?.diagramaEquipamentos?.nodes,
      prevData?.diagramaEquipamentos?.edges,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só resetDiagramData é usado; o objeto diagrama inteiro reintroduziria o loop de re-render do report (Task 7)
  }, [
    restaurarPrevData,
    setEditMode,
    setChanged,
    setShowAllContacts,
    setDeletedAnexos,
    fetchAnexosCircuito,
    diagrama?.resetDiagramData,
    prevData,
  ]);

  // Reporta os handlers/loading ao cabeçalho de serviceItem.js (substitui o ref imperativo removido na Task 7)
  // Cleanup zera ao desmontar/trocar de circuito, senão o cabeçalho fica com handlers de um circuito que já sumiu
  useEffect(() => {
    // abaAtiva/onTrocarAba (setAbaAtiva) somam-se ao report: a barra de abas visível
    // fica em serviceItem.js, fora do Paper do circuito — ver comentário em CircuitoTabs
    setCircuitoActions({
      handleSave,
      handleReset,
      loading,
      abaAtiva,
      onTrocarAba: setAbaAtiva,
    });
    return () =>
      setCircuitoActions({
        handleSave: null,
        handleReset: null,
        loading: false,
        abaAtiva: 0,
        onTrocarAba: null,
      });
  }, [handleSave, handleReset, loading, abaAtiva, setCircuitoActions]);

  return (
    <Box>
      <CircuitoTabs abaAtiva={abaAtiva}>
        {{
          formulario: (
            <Grid container spacing={1}>
              {/* Rede, Tipos de Entrega, Cliente Final, Parceiro, Velocidade,
              POP e Regional, Equipamentos e Meio de Entrega */}
              <FormularioTab
                index={index}
                formData={formData}
                setFormData={setFormData}
                setPrevData={setPrevData}
                errors={errors}
                handleChange={handleChange}
                handleNestedChange={handleNestedChange}
                validateBlocoAndIp={validateBlocoAndIp}
                temVelocidade={temVelocidade}
                editMode={editMode}
                loading={loading}
                setLoading={setLoading}
                loadingSerial={loadingSerial}
                delivery={delivery}
                setChanged={setChanged}
                showAllContacts={showAllContacts}
                setShowAllContacts={setShowAllContacts}
                serviceId={serviceId}
                clientCpes={clientCpes}
                optionsRoteamento={optionsRoteamento}
                setOptionsRoteamento={setOptionsRoteamento}
                optionsEntregaFinal={optionsEntregaFinal}
                setOptionsEntregaFinal={setOptionsEntregaFinal}
                optionsEntregaFinalCliente={optionsEntregaFinalCliente}
                setOptionsEntregaFinalCliente={setOptionsEntregaFinalCliente}
                valueRoteamento={valueRoteamento}
                setValueRoteamento={setValueRoteamento}
                valueEntregaFinal={valueEntregaFinal}
                setValueEntregaFinal={setValueEntregaFinal}
                valueEntregaFinalCliente={valueEntregaFinalCliente}
                setValueEntregaFinalCliente={setValueEntregaFinalCliente}
                updateOptions={updateOptions}
                setSnackbar={setSnackbar}
                popTags={popTags}
                regionalTags={regionalTags}
                loadingTags={loadingTags}
                selectedPopId={selectedPopId}
                handlePopSelect={handlePopSelect}
                handlePopCreate={handlePopCreate}
                selectedRegionalId={selectedRegionalId}
                handleRegionalSelect={handleRegionalSelect}
                handleRegionalCreate={handleRegionalCreate}
                handleTagUpdate={handleTagUpdate}
                handleTagDelete={handleTagDelete}
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} sx={styles.gritItemTitle}>
                <EditNoteOutlined color="primary" />
                <Typography variant="mySubtitle2">Observações Finais</Typography>
              </Grid>
              <Grid container spacing={1} sx={styles.contentIdentation}>
                <Grid item xs={12}>
                  <TextField
                    sx={styles.textField}
                    disabled={!editMode || loading}
                    autoComplete="off"
                    multiline
                    fullWidth
                    size="small"
                    label="Descreva as observações"
                    value={formData?.observations}
                    name="observations"
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Grid>
          ),
          anexos: (
            <Grid container spacing={1}>
              <AnexosTab
                circuitoAnexos={circuitoAnexos}
                setCircuitoAnexos={setCircuitoAnexos}
                setDeletedAnexos={setDeletedAnexos}
                editMode={editMode && !loading}
                isActive={isActive && abaAtiva === 1}
                inputId={`input-upload-circuito-${formData?.id || formData?.tempId}`}
                setSnackbar={setSnackbar}
              />
            </Grid>
          ),
          diagrama: (
            <Grid container spacing={1}>
              <DiagramaTab
                diagrama={diagrama}
                nodeAnexos={nodeAnexos}
                diagramInstanceKey={diagramInstanceKey}
                editMode={editMode && !loading}
                isActive={isActive && abaAtiva === 2}
                isExpanded={isDiagramExpanded}
                onToggleExpanded={() => setIsDiagramExpanded((prev) => !prev)}
                circuitoPersisted={Boolean(formData?.id)}
                circuitoId={formData?.id || ""}
                hostOptions={hostOptions}
                setSnackbar={setSnackbar}
                // Botão Salvar do diagrama ampliado (mesmo handleSave/loading do cabeçalho, sem duplicar lógica)
                onSalvarCircuito={handleSave}
                salvandoCircuito={loading}
              />
            </Grid>
          ),
        }}
      </CircuitoTabs>
      {/* Campos Personalizados */}
      {/*  {formData?.customFields?.map((field, fieldIndex) => (
          <React.Fragment key={fieldIndex}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="mySubtitle2">{field?.title}</Typography>
            </Grid>

            {field?.content?.map((item, itemIndex) => (
              <Grid item xs={12} sm={6} key={itemIndex}>
                {Number(field?.type) === 0 ? (
                  <FormControlLabel
                    disabled={!editMode || loading}
                    control={
                      <Checkbox
                        checked={item?.value}
                        onChange={(e) => {
                          handleCustomFieldsChange({
                            e,
                            fieldIndex,
                            itemIndex,
                          });
                        }}
                      />
                    }
                    label={item?.label}
                  />
                ) : (
                  <TextField sx={styles.textField}
                    disabled={!editMode || loading}
                    fullWidth
                    autoComplete="off"
                    size="small"
                    label={item?.label}
                    value={item?.value}
                    onChange={(e) => {
                      handleCustomFieldsChange({
                        e,
                        fieldIndex,
                        itemIndex,
                      });
                    }}
                  />
                )}
              </Grid>
            ))}
          </React.Fragment>
        ))} */}
    </Box>
  );
}
